import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { cn } from "cn";
import { capitalize, groupBy } from "lodash-es";
import { Fragment, useState } from "react";
import { useUnitTypes } from "@/api/use-unit-types";
import { useComboboxOpen } from "@/components/ui/combobox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { BwCheckmarkLine, BwChevronDownLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import Typeahead from "@/components/ui/type-ahead";
import { Typography } from "@/components/ui/typography";
import { UnitTypeGroupEnum } from "@/types/restful";

const sortGroup = (a: any, b: any) => {
  const sortOrder = [
    UnitTypeGroupEnum.QUANTITY,
    UnitTypeGroupEnum.LENGTH,
    UnitTypeGroupEnum.AREA,
    UnitTypeGroupEnum.VOLUME,
    UnitTypeGroupEnum.WEIGHT,
    UnitTypeGroupEnum.TIME,
    UnitTypeGroupEnum.PACKAGED,
  ];

  return sortOrder.indexOf(a[0]) - sortOrder.indexOf(b[0]);
};

type UnitTypeSelectProps = FieldWrapperInjectedProps & {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  "data-test"?: string;
  sideOffset?: number;
};

export function UnitTypeSelect({
  value,
  onChange,
  disabled,
  className,
  open,
  defaultOpen,
  onOpenChange,
  modal = true,
  sideOffset = 4,
  ...props
}: UnitTypeSelectProps) {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setOpen] = useComboboxOpen({
    open,
    defaultOpen,
    onOpenChange,
    disabled,
  });
  const { data } = useUnitTypes();
  const unitTypeGroup = groupBy(data, "group");

  const selectedUnitType = data?.find(
    (unitType: any) => unitType.key === value,
  );
  return (
    <Popover modal={modal} open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger
        disabled={disabled}
        data-test="line-item-unit-type-select-trigger"
        className="group"
        {...props}
      >
        <div
          className={cn(
            /* sorted */
            "flex",
            "h-10 items-center gap-2 rounded-lg",
            "px-3",
            "group-data-[state=open]:outline-outline-dark-max group-data-[state=open]:outline-[1.5px]",
            "group-aria-invalid:outline-outline-negative-max group-aria-invalid:outline-[1.5px] group-aria-invalid:group-data-[state=open]:outline-outline-negative-max",
            disabled ? "cursor-default" : "cursor-pointer",
            className,
          )}
        >
          <Typography size="sm" className="text-text-dark-max text-left grow">
            {selectedUnitType?.name}
          </Typography>
          <div className="flex items-center gap-2 self-stretch border-l border-border-dark-low pl-2">
            <Typography size="sm" className="text-text-dark-max ">
              {selectedUnitType?.alias}
            </Typography>
            <BwChevronDownLine className="text-text-dark-min" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="end" sideOffset={sideOffset}>
        <Command>
          <CommandInput placeholder="Search..." onValueChange={setInputValue} />
          <CommandList>
            <CommandEmpty>No unit type found.</CommandEmpty>

            {Object.entries(unitTypeGroup)
              .sort(sortGroup)
              .map(([group, options]: any) => (
                <Fragment key={group}>
                  <CommandGroup heading={capitalize(group)} className="py-0">
                    {options.map((option: any) => (
                      <CommandItem
                        value={`${option?.name} (${option?.alias})`}
                        key={option.key}
                        onSelect={() => {
                          onChange(option?.key);
                          setOpen(false);
                        }}
                      >
                        <Typography size="sm" className="grow">
                          <Typeahead
                            content={`${option?.name} (${option?.alias})`}
                            keyword={inputValue}
                          />
                        </Typography>
                        <BwCheckmarkLine
                          size={16}
                          className={cn(
                            /* sorted */
                            "shrink-0",
                            value === option.key ? "opacity-100" : "opacity-0",
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandSeparator />
                </Fragment>
              ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
